import type { JSX } from 'react';

import TextLegacy from 'DesignSystem2/Text';
import Body from 'DesignSystem3/Text/Body';

export interface NotificationPreferenceVo {
    email_preferences: NotificationPreferences;
    push_preferences?: NotificationPreferences;
}

export type NotificationType =
    | ''
    | 'taker_trade'
    | 'maker_trade'
    | 'market_open'
    | 'position_settled'
    | 'ranged_position_settled'
    | 'trading_resumed'
    | 'trading_paused'
    | 'order_auto_canceled'
    | 'deposit_requested'
    | 'deposit_applied'
    | 'deposit_failed'
    | 'deposit_returned'
    | 'immediate_deposit_applied'
    | 'immediate_deposit_failed'
    | 'withdrawal_requested'
    | 'withdrawal_applied'
    | 'withdrawal_returned'
    | 'market_price_movement'
    | 'perps_alerts'
    | 'custom_price_target'
    | 'portfolio_value_movement'
    | 'referral_reward_ready'
    | 'referral_first_deposit'
    | 'incentive_program_created'
    | 'rfq_created'
    | 'communication_rfq_created_all'
    | 'communication_rfq_created_favorited'
    | 'communication_quote_created'
    | 'communication_quote_accepted'
    | 'social_account_post_notification'
    | 'social_account_trade_notification'
    | 'social_account_position_notification'
    | 'inner_circle_request_received'
    | 'inner_circle_request_accepted'
    | 'inner_circle_member_added'
    | 'inner_circle_request_received_daily_rollup'
    | 'inner_circle_member_added_daily_rollup'
    | 'promotional'
    | 'personalized_sports_notification'
    | 'compliance_alert';

export type NotificationSettings = Record<string, any>;

// Perps-alert deny keys in the perps_alerts settings JSONB; absent/false => that
// category sends (opt-out, default on). Mirror of backend model.Perps*DisabledKey.
export const PERPS_ALERT_DISABLED_KEYS = [
    'disabled_nearing_liquidation',
    'disabled_liquidation_executed',
    'disabled_exit_trigger',
] as const;

export type PerpsAlertDisabledKey = (typeof PERPS_ALERT_DISABLED_KEYS)[number];

// Self-match-prevention (SMP) deny key in the order_auto_canceled settings
// JSONB; absent/false => SMP auto-cancel notifications send (opt-out, default
// on, no backfill). Mirror of backend model.OrderAutoCancelSelfTradeDisabledKey.
// See EX-298.
export const ORDER_AUTO_CANCEL_SELF_TRADE_DISABLED_KEY = 'disabled_self_trade';

export type NotificationPreferences = Array<NotificationType>;

export interface NotificationCategory {
    name: string;
    description: string | JSX.Element;
    notificationTypes: NotificationPreferences;
}

export const GeneralNotificationCategories: Array<NotificationCategory> = [
    {
        name: 'Transfers',
        description: 'Deposit and withdrawal notifications.',
        notificationTypes: [
            'deposit_applied',
            'deposit_failed',
            'deposit_returned',
            'immediate_deposit_applied',
            'withdrawal_applied',
            'withdrawal_returned',
        ],
    },
    {
        name: 'Referral rewards',
        description: 'Get notified when referral rewards are ready to claim.',
        notificationTypes: ['referral_reward_ready'],
    },
    {
        name: 'Referral activity',
        description: 'Get notified when someone you referred makes their first deposit.',
        notificationTypes: ['referral_first_deposit'],
    },
    {
        name: 'Social activity',
        description: 'Get notified when accounts you follow post, trade, or open positions.',
        notificationTypes: [
            'social_account_post_notification',
            'social_account_trade_notification',
            'social_account_position_notification',
        ],
    },
    {
        name: 'Inner Circle requests',
        description: 'When someone requests to join your Inner Circle',
        notificationTypes: ['inner_circle_request_received'],
    },
    {
        name: 'Inner Circle accepted',
        description: 'When someone accepts your request to join their Inner Circle',
        notificationTypes: ['inner_circle_request_accepted'],
    },
    {
        name: 'Market rewards',
        description: (
            <span>
                <Body.x20>{'Get notified when liquidity or volume rewards are added to markets. '}</Body.x20>
                <TextLegacy.Link.x20
                    newTab
                    href="https://help.kalshi.com/incentive-programs/liquidity-incentive-program"
                >
                    {'Learn more.'}
                </TextLegacy.Link.x20>
            </span>
        ),
        notificationTypes: ['incentive_program_created'],
    },
    // {
    //     name: 'RFQ notifications',
    //     description: 'Get notified when requests for quotes are fulfilled.',
    //     notificationTypes: ['rfq_created'],
    // },
];

export const PositionNotificationCategories: Array<NotificationCategory> = [
    {
        name: 'Price movements',
        description: `Get notified when a market's last traded price moves 30 cents or more.`,
        notificationTypes: ['market_price_movement'],
    },
    {
        name: 'Market settlements',
        description: 'Get notified about settlements of markets you have positions in.',
        notificationTypes: ['position_settled', 'ranged_position_settled'],
    },
    {
        name: 'Order auto-cancellation',
        description:
            'Get notified when the exchange cancels your orders, generally due to insufficient funds or risk limits.',
        notificationTypes: ['order_auto_canceled'],
    },
    // Taker-side trade confirmation is rendered by TakerTradeNotificationRow
    // (toggle + threshold) instead of the generic emailToggleRow, since it
    // supports per-rule conditions in the v2 rule engine.
];

export const RFQNotificationCategories: Array<NotificationCategory> = [
    {
        name: 'RFQ creation (all)',
        description: `Get notified when an RFQ is created in any market.`,
        notificationTypes: ['communication_rfq_created_all'],
    },
    {
        name: 'RFQ creation (watchlisted series)',
        description: "Get notified when an RFQ is created in a series you've added to your watchlist.",
        notificationTypes: ['communication_rfq_created_favorited'],
    },
    {
        name: 'Quote creation',
        description: 'Get notified when a quote is created in response to one of your RFQs.',
        notificationTypes: ['communication_quote_created'],
    },
    {
        name: 'Quote acceptance',
        description: 'Get notified when one of your quotes has been accepted.',
        notificationTypes: ['communication_quote_accepted'],
    },
];

export const PromotionalNotificationCategories: Array<NotificationCategory> = [
    {
        name: 'Breaking events',
        description: 'Get notified about major news and trending events.',
        notificationTypes: ['promotional'],
    },
    {
        name: 'Sports',
        description: 'Follow major sports events and your favorite football teams.',
        notificationTypes: ['personalized_sports_notification'],
    },
    {
        name: 'New markets',
        description: 'Weekly digest of new markets.',
        notificationTypes: ['market_open'],
    },
];

export interface UserGetNotificationSettingsResponse {
    batch_interval: number;
    settings: NotificationSettings;
}
